import { loadTransformationTypes } from 'app/actions/dictionaries.actions';
import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { checkAccess } from 'app/actions/app.actions';
import { initGrid } from 'utils/grid/grid';
import {
  applyRecipient,
  loadEmailList,
  loadReportList,
  loadSftpList,
  removeRecipient,
  saveEmailList,
  saveReportList,
  saveSftpList,
} from 'report-subscriptions/actions/report-subscriptions-page.actions';
import template from './report-subscriptions.html';

const DIRTY_FORM = 'reportSubscription';

const mapStateToThis = (state) => ({
  defaultContactPhone: state.contact.lottery.defaultContactPhone,
  isLoadingReports: state.reportSubscriptionsPage.isLoadingReports,
  isLoadingEmail: state.reportSubscriptionsPage.isLoadingEmail,
  itemsReports: state.reportSubscriptionsPage.itemsReports,
  itemsEmail: state.reportSubscriptionsPage.itemsEmail,
  subscriptionsReports: state.reportSubscriptionsPage.subscriptionsReports,
  subscriptionsEmail: state.reportSubscriptionsPage.subscriptionsEmail,
  metaEmail: state.reportSubscriptionsPage.metaEmail,
  itemsSftp: state.reportSubscriptionsPage.itemsSftp,
  subscriptionsSftp: state.reportSubscriptionsPage.subscriptionsSftp,
  metaSftp: state.reportSubscriptionsPage.metaSftp,
  isLoadingSftp: state.reportSubscriptionsPage.isLoadingSftp,
  recipients: state.reportSubscriptionsPage.recipients,
  transformationTypes: state.dictionaries.transformationTypes,
  session: state.session,
  applicationName: state.config.applicationName[state.session.locale],
  usesOptInReports: state.config.usesOptInReports,
});

const mapDispatchToThis = {
  loadTransformationTypes,
  loadReportList,
  loadEmailList,
  loadSftpList,
  checkAccess,
  saveEmailList,
  saveReportList,
  saveSftpList,
  applyRecipient,
  removeRecipient,
  setDirtyForm,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', 'gettextCatalog', 'Notifications')
class ReportSubscriptionsPageController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.DIRTY_FORM = DIRTY_FORM;
  }

  $onInit() {
    this.retailerId = this.$state.params.retailerId;
    this.retailerNumber = this.$state.params.retailerNumber;
    this.retailerType = this.$state.params.retailerType;

    this.text = {
      report: {
        dialogTitle: this.gettextCatalog.getString('Success!'),
        saveSuccess: this.gettextCatalog.getString(
          `<p>You have successfully updated your report subscriptions.</p><p>Modifications to subscriptions will take effect the next day for each report.</p>`
        ),
        validateTitle: this.gettextCatalog.getString('Error!'),
        validateError: this.gettextCatalog.getString('You can select up to two file types in one report'),
      },
      email: {
        dialogTitle: this.gettextCatalog.getString('Success!'),
        saveSuccess: this.gettextCatalog.getString(
          `<p>You have successfully updated your email subscriptions.</p><p>Modifications to subscriptions will take effect the next day for each report.</p>`
        ),
      },
      sftp: {
        dialogTitle: this.gettextCatalog.getString('Success!'),
        saveSuccess: this.gettextCatalog.getString(
          `<p>You have successfully updated your report subscriptions.</p><p>An email was sent to an administrator with a request to configure the SFTP server.</p><p>The SFTP transfer will not work until the SFTP credentials are provided.</p>`
        ),
      },
    };

    this.tabs = {
      report: {
        load: () => this.loadReportList(this.retailerId, this.retailerType),
        save: () => this.saveReportList(this.retailerId),
      },
      email: {
        load: () => this.loadEmailList(this.retailerId, this.retailerType),
        save: () => this.saveEmailList(this.retailerId),
      },
      sftp: {
        load: () => this.loadSftpList(this.retailerId, this.retailerType),
        save: () => this.saveSftpList(this.retailerId),
      },
    };

    Promise.all([this.loadTransformationTypes()]).then(() => {
      this.headers = [
        {
          text: this.gettextCatalog.getString('Report Name'),
          field: 'reportName',
          sortable: false,
        },
        {
          text: this.gettextCatalog.getString('Reporting Period'),
          field: 'reportPeriod',
          sortable: false,
        },
        {
          text: '',
          sortable: false,
        },
      ];

      this.canShowReports = this.session.retailer.retailerType;
      this.canShowReportSubscriptions = this.usesOptInReports && this.checkAccess('reportSubscriptions.view') && this.canShowReports;
      this.canShowEmailSubscriptions = this.checkAccess('reportSubscriptions.view.email') && this.canShowReports;
      this.canShowSftpSubscriptions = this.checkAccess('reportSubscriptions.view.sftp') && this.canShowReports;

      if (this.canShowReportSubscriptions) {
        this.onTabSelect('report');
        this.gridModel = initGrid(this.loadEmailList, this.defaultParams);
      } else if (this.canShowEmailSubscriptions) {
        this.onTabSelect('email');
        this.gridModel = initGrid(this.loadEmailList, this.defaultParams);
      } else if (this.canShowSftpSubscriptions) {
        this.onTabSelect('sftp');
        this.gridModel = initGrid(this.loadSftpList, this.defaultParams);
      }
    });
  }

  $onDestroy() {
    this.unsubscribe();
  }

  submit(tabId) {
    const text = this.text[tabId];
    this.tabs[tabId].save().then(() => {
      this.setDirtyForm(this.DIRTY_FORM, false);
      this.form.$setPristine();
      return this.Notifications.showAlert(text.dialogTitle, text.saveSuccess);
    });
  }

  onTabSelect(tabId, form) {
    if (form) {
      form.$setPristine();
    }
    this.activeTabId = tabId;
    this.tabs[tabId].load().then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  change() {
    this.setDirtyForm(this.DIRTY_FORM, true);
  }

  changeReport(fileTypeId, format) {
    // validate selection
    let subscribedNumber = 0;
    Object.keys(this.subscriptionsReports[fileTypeId].transformations).forEach((key) => {
      if (this.subscriptionsReports[fileTypeId].transformations[key].subscribed) {
        subscribedNumber += 1;
      }
    });

    if (subscribedNumber > 2) {
      this.Notifications.showAlert(this.text.report.validateTitle, this.text.report.validateError);
      this.subscriptionsReports[fileTypeId].transformations[format].subscribed =
        !this.subscriptionsReports[fileTypeId].transformations[format].subscribed;
    }

    this.setDirtyForm(this.DIRTY_FORM, true);
  }

  cancel() {
    this.onTabSelect(this.activeTabId);
  }

  onGroupChange() {
    this.tabs[this.activeTabId].load().then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }
}

export default {
  template,
  controller: ReportSubscriptionsPageController,
};



// WEBPACK FOOTER //
// ./src/modules/report-subscriptions/containers/page/report-subscriptions-page-component.js